import { type DeleteLabelGroupMutation } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { deleteLabelGroupMutationDocument } from "./label-group.gql";
import { EVENT_LABEL_GROUP_DELETED, trackLabelEvent } from "./tracking";

type DeletedLabelGroup = DeleteLabelGroupMutation["deleteLabelGroup"];

export interface DeleteLabelGroupInput {
  id: string;
}

export interface UseDeleteLabelGroupProps {
  onCompleted?: (data: DeletedLabelGroup) => void;
  onError?: (e: Error) => void;
}

export const useDeleteLabelGroup = ({ onCompleted, onError }: UseDeleteLabelGroupProps) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: DeleteLabelGroupInput) => {
      const client = createOperationClient("deleteLabelGroup");
      const data = await client.request(deleteLabelGroupMutationDocument, { input });

      return data.deleteLabelGroup;
    },
    onSuccess: (labelGroup) => {
      // Remove the label group from cache
      queryClient.removeQueries({
        queryKey: queryKeys.labelGroups.detail(labelGroup.id),
      });

      // Invalidate all related queries to refetch without the deleted item
      queryClient.invalidateQueries({
        queryKey: queryKeys.labelGroups.list(labelGroup.workspaceId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.labels.list(labelGroup.workspaceId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.labels.instances(),
      });

      // Track the event
      trackLabelEvent(EVENT_LABEL_GROUP_DELETED, labelGroup);

      // Call the onCompleted callback
      if (onCompleted) {
        onCompleted(labelGroup);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    deleteLabelGroup: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
